import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api, ApiError, type ProfileMatch } from '../api.js';
import AppHeader from '../components/AppHeader.js';
import Avatar from '../components/Avatar.js';
import AvatarUploader from '../components/AvatarUploader.js';
import TeamBadge from '../components/TeamBadge.js';
import { formatKickoff } from '../lib/intl.js';
import { useOwnParticipantId } from '../lib/useOwnParticipantId.js';
import styles from './ProfileScreen.module.css';

function HistoryCard({ match, locale }: { match: ProfileMatch; locale: string }) {
  const { t } = useTranslation();
  const kickoff = formatKickoff(match.kickoffAt, locale);
  const penaltyTeam =
    match.prediction.penaltyWinner === 'home'
      ? match.homeTeam?.name
      : match.prediction.penaltyWinner === 'away'
        ? match.awayTeam?.name
        : null;

  return (
    <div className={styles.card}>
      <div className={styles.cardTopbar}>
        <span className={styles.stagePill}>{match.stageName}</span>
        {kickoff ? <span className={styles.kickoff}>{kickoff}</span> : null}
      </div>

      <div className={styles.cardBody}>
        <div className={styles.teams}>
          <div className={styles.team}>
            <TeamBadge team={match.homeTeam} />
            <span className={styles.teamName}>{match.homeTeam?.name ?? '—'}</span>
          </div>
          <span className={styles.predictedScore}>
            {match.prediction.home} × {match.prediction.away}
          </span>
          <div className={`${styles.team} ${styles.teamAway}`}>
            <span className={styles.teamName}>{match.awayTeam?.name ?? '—'}</span>
            <TeamBadge team={match.awayTeam} />
          </div>
        </div>

        {penaltyTeam ? <p className={styles.penaltyNote}>({penaltyTeam})</p> : null}

        {match.status === 'finished' ? (
          <div className={styles.finishedRow}>
            <span className={styles.realScore}>
              <span className={styles.realScoreLabel}>{t('predictions.finalScore')}</span>{' '}
              {match.realHome} × {match.realAway}
            </span>
            {match.score ? (
              <div className={styles.scoreBadges}>
                <span className={styles.ptsBadge}>
                  {t('leaderboard.points', { count: match.score.total })}
                </span>
                <span className={`${styles.ptBadge} ${styles.swatchExact}`}>
                  {match.score.exact}
                </span>
                <span className={`${styles.ptBadge} ${styles.swatchWinner}`}>
                  {match.score.outcome}
                </span>
                <span className={`${styles.ptBadge} ${styles.swatchProx}`}>
                  {match.score.proximity}
                </span>
                <span className={`${styles.ptBadge} ${styles.swatchPenalty}`}>
                  {match.score.penalty}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function ProfileScreen() {
  const { t, i18n } = useTranslation();
  const { id = '', poolId = '', participantId = '' } = useParams();
  const locale = i18n.resolvedLanguage ?? 'en';
  const queryClient = useQueryClient();

  const { data: pool } = useQuery({
    queryKey: ['pool', id, poolId],
    queryFn: () => api.getPool(id, poolId),
  });
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['profile', id, poolId, participantId, locale],
    queryFn: () => api.getParticipantProfile(id, poolId, participantId, locale),
  });

  // Changing your photo is only offered on your *own* profile — someone
  // else's is a read-only record of what they predicted. Circle membership
  // and bolão participation are different lists now, so "own" can only be
  // resolved from this bolão's own participant list, not a circle roster.
  const ownParticipantId = useOwnParticipantId(id, poolId, pool);
  const isOwnProfile = ownParticipantId === participantId;

  if (isPending) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="screen">
        <Link to={`/groups/${id}/bolaos/${poolId}/leaderboard`} className="muted">
          {t('common.back')}
        </Link>
        <p className="error">
          {error instanceof ApiError ? error.message : t('common.somethingWentWrong')}
        </p>
      </main>
    );
  }

  return (
    <main className="screen">
      <AppHeader groupId={id} poolId={poolId} />
      <Link to={`/groups/${id}/bolaos/${poolId}/leaderboard`} className="muted">
        {t('common.back')}
      </Link>

      <div className={styles.header}>
        {isOwnProfile ? (
          <AvatarUploader
            name={data.displayName}
            initialImageUrl={data.image}
            size={72}
            stacked
            onUploaded={() =>
              void queryClient.invalidateQueries({
                queryKey: ['profile', id, poolId, participantId],
              })
            }
          />
        ) : (
          <Avatar name={data.displayName} imageUrl={data.image} size={72} />
        )}
        <div>
          <h1 className={styles.name}>
            {data.displayName}
            {data.isOwner ? <span className={styles.tag}>{t('groups.organizer')}</span> : null}
          </h1>
          {pool ? <p className="muted">{pool.name}</p> : null}
        </div>
      </div>

      {data.matches.length === 0 ? (
        <p className="muted">{t('profile.noHistory')}</p>
      ) : (
        <div className={styles.list}>
          {data.matches.map((match) => (
            <HistoryCard key={match.id} match={match} locale={locale} />
          ))}
        </div>
      )}
    </main>
  );
}
